import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CloudinaryPicture, WIDTH_LADDERS } from 'components/rich';

import tracker from '../../lib/tracker';

// Building blocks shared by the redesigned landing pages (/gifts, /gifts-under-*, /display).
// Styles: styles/_landing.scss (lnd- prefix).

// Product gallery photos (products/ ladder, up to 640 px) and the photo book landing set.
export const productImage = (key) => ({
  imageKey: key,
  prefix: 'products/',
  widths: WIDTH_LADDERS.products,
});
export const landingImage = (name) => ({
  imageKey: `landing/photo-books/${name}`,
  widths: WIDTH_LADDERS.landing,
});

export const imageShape = PropTypes.shape({
  imageKey: PropTypes.string.isRequired,
  prefix: PropTypes.string,
  // Folders without a width ladder (sku/) pass a variant `type` instead of `widths`.
  widths: PropTypes.arrayOf(PropTypes.number),
  type: PropTypes.string,
});

// Returns `track(cta, location, target)`, which builds an onClick handler for
// `<campaign>_cta_click`. `extra` is added to every event (e.g. the budget).
export const ctaTracker =
  (campaign, extra = {}) =>
  (cta, location, target) =>
  () => {
    tracker.onCustomEvent(`${campaign}_cta_click`, { ...extra, cta, location, target });
  };

// Without `alt` the image takes its description from the alt catalog (images.json). Pass
// alt="" for decorative photos and photos inside links.
export const Picture = ({ image, sizes, alt, priority }) => (
  <CloudinaryPicture
    imageKey={image.imageKey}
    prefix={image.prefix}
    widths={image.widths}
    sizes={image.widths ? sizes : undefined}
    type={image.type}
    alt={alt}
    priority={priority}
    loading={priority ? undefined : 'lazy'}
  />
);

Picture.propTypes = {
  image: imageShape.isRequired,
  sizes: PropTypes.string.isRequired,
  alt: PropTypes.string,
  priority: PropTypes.bool,
};

// Phones get the shorter copy from the mobile frames, both stay in the DOM.
export const Responsive = ({ desktop, phone }) =>
  phone && phone !== desktop ? (
    <>
      <span className="lnd-desk">{desktop}</span>
      <span className="lnd-phone">{phone}</span>
    </>
  ) : (
    desktop
  );

Responsive.propTypes = {
  desktop: PropTypes.node.isRequired,
  phone: PropTypes.node,
};

export const PriceTag = ({ price, className }) =>
  price ? (
    <span className={className}>
      {/* Screen readers hear "Original price from $22, now $16.50" instead of two bare amounts. */}
      {price.originalPrice && <span className="sr-only">Original price </span>}
      {price.priceFrom && <span className="lnd-price-from">from </span>}
      {price.originalPrice && (
        <>
          <s className="lnd-price-original">{price.originalPrice}</s>
          <span className="sr-only">, now</span>
        </>
      )}{' '}
      <span className={price.originalPrice ? 'lnd-price-sale' : undefined}>{price.price}</span>
    </span>
  ) : null;

PriceTag.propTypes = {
  price: PropTypes.shape({
    price: PropTypes.string,
    originalPrice: PropTypes.string,
    priceFrom: PropTypes.bool,
  }),
  className: PropTypes.string,
};

export const SectionHead = ({ eyebrow, title, aside, className, eyebrowClassName }) => (
  <div className={classNames('lnd-head', className)}>
    <div>
      <p className={classNames('lnd-eyebrow', eyebrowClassName)}>{eyebrow}</p>
      <h2 className="lnd-h2">{title}</h2>
    </div>
    {aside && <p className="lnd-aside">{aside}</p>}
  </div>
);

SectionHead.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  aside: PropTypes.node,
  className: PropTypes.string,
  eyebrowClassName: PropTypes.string,
};
